import React, { useRef, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
} from 'chart.js';
import { API_BASE_URL } from '../../services/api';
import ReportViewer from './ReportViewer';

/**
 * Renders a ReportViewer offscreen (visually hidden but in DOM so charts render),
 * waits for Chart.js canvases to be ready, captures the DOM as self-contained HTML,
 * then calls onCapture(html).
 */
const OffscreenReportRenderer = ({ data, onCapture }) => {
  const containerRef = useRef(null);
  const capturedRef = useRef(false);

  // Dummy chart refs — we don't need to export them, just let them render
  const perfRef = useRef(null);
  const avsRef = useRef(null);
  const atRef = useRef(null);
  const adRef = useRef(null);
  const haRef = useRef(null);
  const crRef = useRef(null);
  const dynRefs = useRef({});

  // Fetch an image URL as an inline data URL (handles CORS via credentials)
  const fetchImageAsDataUrl = useCallback(async (url) => {
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  const captureHTML = useCallback(async () => {
    if (capturedRef.current) return;
    const reportEl = containerRef.current?.querySelector('.report-pages');
    if (!reportEl) return;

    const clone = reportEl.cloneNode(true);

    // Step 1: Convert existing <img> elements to inline data URLs BEFORE canvas
    // conversion so that index-based matching between live and clone is correct.
    const liveImages = reportEl.querySelectorAll('img');
    const cloneImages = clone.querySelectorAll('img');
    const imgPromises = [];
    liveImages.forEach((img, i) => {
      if (!cloneImages[i]) return;
      const cloneImg = cloneImages[i];

      // Use the original branded URL if onError has already swapped to fallback
      const originalSrc = img.getAttribute('data-original-src');
      let fetchSrc = originalSrc || img.src;

      // Strip API_BASE_URL so fetch goes through same-origin (Vite proxy in dev)
      if (API_BASE_URL && fetchSrc.startsWith(API_BASE_URL)) {
        fetchSrc = fetchSrc.slice(API_BASE_URL.length);
      }

      // Try synchronous canvas draw first (works for same-origin images).
      // Skip if we have an original src that differs from current (onError fired).
      if (!originalSrc || originalSrc === img.src) {
        try {
          if (img.complete && img.naturalWidth > 0) {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            cloneImg.src = c.toDataURL('image/png');
            return;
          }
        } catch (e) { /* CORS — fall through to async fetch */ }
      }

      // Async fallback: fetch via same-origin proxy
      if (fetchSrc && !fetchSrc.startsWith('data:')) {
        imgPromises.push(
          fetchImageAsDataUrl(fetchSrc)
            .then(dataUrl => { cloneImg.src = dataUrl; })
            .catch(() => {
              return fetchImageAsDataUrl('/logo-name.png')
                .then(dataUrl => { cloneImg.src = dataUrl; })
                .catch(() => { /* give up */ });
            })
        );
      }
    });
    await Promise.all(imgPromises);

    // Step 2: Convert canvas charts to high-DPI images, marked with data-chart-img
    const liveCanvases = reportEl.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    liveCanvases.forEach((canvas, i) => {
      try {
        const chartInstance = ChartJS.getChart(canvas);
        let dataUrl;
        if (chartInstance) {
          const origDPR = chartInstance.options.devicePixelRatio;
          const origAnim = chartInstance.options.animation;
          // Freshly mounted charts may still be animating. While the animator
          // runs, resize() DEFERS the retina rescale and update('none') doesn't
          // draw synchronously — toDataURL would grab the stale 1x canvas.
          // Stop the animator and disable animation to force a synchronous 3x render.
          chartInstance.options.animation = false;
          chartInstance.options.devicePixelRatio = 3;
          chartInstance.stop();
          chartInstance.resize();
          chartInstance.update('none');
          dataUrl = canvas.toDataURL('image/png', 1.0);
          if (canvas.width < canvas.clientWidth * 2) {
            console.warn('[pdf-capture] high-DPI re-render did not apply for', canvas);
          }
          chartInstance.options.devicePixelRatio = origDPR;
          chartInstance.options.animation = origAnim;
          chartInstance.resize();
          chartInstance.update('none');
        } else {
          dataUrl = canvas.toDataURL('image/png', 1.0);
        }
        const img = document.createElement('img');
        img.src = dataUrl;
        img.setAttribute('data-chart-img', 'true');
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.display = 'block';
        if (cloneCanvases[i]?.parentNode) {
          cloneCanvases[i].parentNode.replaceChild(img, cloneCanvases[i]);
        }
      } catch (e) {
        console.warn('Could not convert canvas to image:', e);
      }
    });

    // Step 3: Inline CSS
    const styleBlocks = [];
    for (const sheet of document.styleSheets) {
      try {
        const rules = [];
        for (const rule of sheet.cssRules) {
          if (rule.type === CSSRule.IMPORT_RULE ||
              rule.type === CSSRule.CHARSET_RULE ||
              rule.type === CSSRule.NAMESPACE_RULE) continue;
          rules.push(rule.cssText);
        }
        if (rules.length > 0) {
          styleBlocks.push(`<style>${rules.join('\n')}</style>`);
        }
      } catch (e) { /* CORS — skip */ }
    }

    // Step 4: Inject CSS variable overrides from inline style
    const inlineStyle = reportEl.getAttribute('style') || '';
    const varOverrides = inlineStyle
      .split(';')
      .filter(s => s.trim().startsWith('--'))
      .join('; ');
    if (varOverrides) {
      styleBlocks.push(`<style>.report-pages { ${varOverrides} }</style>`);
    }

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
${styleBlocks.join('\n')}
</head>
<body class="print-capture">
<div class="portfolio-reports-app report-pages print-capture-pages" style="${inlineStyle.replace(/"/g, '&quot;')}">
${clone.innerHTML}
</div>
</body>
</html>`;

    capturedRef.current = true;
    onCapture(html);
  }, [onCapture, fetchImageAsDataUrl]);

  // Poll for chart readiness — charts need a frame or two to render
  useEffect(() => {
    if (!data) return;
    capturedRef.current = false;

    // Wait for charts to render, then capture
    let attempts = 0;
    const maxAttempts = 30; // 3 seconds max
    const timer = setInterval(() => {
      attempts++;
      const canvases = containerRef.current?.querySelectorAll('canvas');
      // Charts are ready when canvases exist and have non-zero dimensions
      const images = containerRef.current?.querySelectorAll('img');
      const imagesReady = !images || images.length === 0 ||
        Array.from(images).every(img => img.complete);
      const ready = canvases && canvases.length > 0 &&
        Array.from(canvases).every(c => c.width > 0 && c.height > 0) &&
        imagesReady;

      if (ready || attempts >= maxAttempts) {
        clearInterval(timer);
        // One more frame to ensure paint is complete
        requestAnimationFrame(() => {
          requestAnimationFrame(async () => {
            await captureHTML();
          });
        });
      }
    }, 100);

    return () => clearInterval(timer);
  }, [data, captureHTML]);

  if (!data) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        left: '-9999px',
        top: 0,
        width: '210mm',
        visibility: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <ReportViewer
        data={data}
        performanceChartRef={perfRef}
        allocationVsSaaChartRef={avsRef}
        activeTiltsChartRef={atRef}
        allocationDonutChartRef={adRef}
        historicalAllocationChartRef={haRef}
        creditChartRef={crRef}
        dynamicChartRefs={dynRefs}
      />
    </div>
  );
};

export default OffscreenReportRenderer;
